<template>
  <div className="test">
    <h3>
      Live data
      <a
        v-if="fractalLogo"
        href="https://www.tomatopay.co.uk/"
        target="_blank"
        rel="noopener noreferrer"
        class="small"
        style="font-size:18px"
      >powered by <img
        src="../assets/tomatopay-logo.png"
        alt="tomato pay"
        class="realtime-fractal"
      >
      </a>
    </h3>
    <p class="label mb-0 mt-3">
      <span class="text-apim-fail">Red</span> = failure |
      <span class="text-apim-warn">Yellow</span> = client error |
      <span class="text-apim-pass">Green</span> = pass
    </p>
    <BTable
      class="darkpurple"
      bordered
      hover
      responsive
      :items="realTimeListWithStatuses"
      :fields="fields"
    >
      <template #cell(project_name)="data">
        <LinkToProject
          :project-id="data.item.project_key_str"
          :title="data.item.domain"
          :domain="data.item.domain"
        >
          {{ data.value }}
        </LinkToProject>
      </template>
      <template #cell(results)="data">
        <div class="status-container">
          <LinkToProject
            v-for="status in data.item.statuses"
            :key="status.date.toString()"
            :title="statusTooltip(status)"
            :path="resultsPath(status)"
            :project-id="data.item.project_key_str"
            :class="getClasses(status)"
            :domain="data.item.domain"
          />
        </div>
      </template>
      <template #head(results)="data">
        {{ data.label }}
        <span
          class="label d-flex justify-content-between"
        >
          <span>
            Each block represents an hour – whole line is the last 24 hours
          </span>
          <span>
            Most recent &rarr;
          </span>
        </span>
      </template>
    </BTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LinkToProject from './LinkToProject.vue';

export default {
  components: {
    LinkToProject,
  },
  data() {
    return {
      fields: [
        {
          key: 'project_name',
          label: 'Provider',
        },
        {
          key: 'results',
          label: 'Last 24 Hours',
        },
      ],
      timer: null,
    };
  },
  computed: {
    ...mapGetters([
      'liveStats',
      'range',
      'fractalLogo',
    ]),
    start() {
      return this.range.start;
    },
    end() {
      return this.range.end;
    },
    realTimeListWithStatuses() {
      return this.liveStats.map((item) => {
        const statuses = this.convertStatusesToList(item.results);
        return { ...item, statuses };
      });
    },
  },
  created() {
    this.timer = setInterval(() => this.loadLiveData(), 5 * 60 * 1000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    this.timer = null;
  },
  methods: {
    ...mapActions([
      'colId',
      'loadLiveData',
    ]),
    convertStatusesToList(statusData) {
      const resultData = statusData || {};
      const results = [];
      const currTime = this.$moment.utc(this.start);
      const endTime = this.$moment.utc(this.end);
      while (currTime < endTime) {
        const key = currTime.format();
        results.push({ date: currTime.clone(), ...(resultData[key] || {}) });
        currTime.add(1, 'HOURS');
      }
      return results;
    },
    getClasses(stats) {
      const classes = ['status-hour'];
      if (!stats) {
        return classes;
      }
      if (stats.failure > 0) {
        if (stats.warning > 0) {
          if (stats.pass > 0) {
            classes.push('status-fail-warn-pass');
          } else {
            classes.push('status-fail-warn');
          }
        } else if (stats.pass > 0) {
          classes.push('status-fail-pass');
        } else {
          classes.push('status-fail');
        }
      } else if (stats.warning > 0) {
        if (stats.pass > 0) {
          classes.push('status-warn-pass');
        } else {
          classes.push('status-warn');
        }
      } else if (stats.pass > 0) {
        classes.push('status-pass');
      } else {
        classes.push('status-nodata');
      }
      return classes.join(' ');
    },
    statusTooltip(stats) {
      let descs = `${stats.date.format('lll')} UTC. \nThere have been `;
      if (!stats) {
        return descs;
      }
      if (stats.failure > 0) {
        if (stats.warning > 0) {
          if (stats.pass > 0) {
            descs += ('failures, warnings and passes');
          } else {
            descs += ('failures and warnings');
          }
        } else if (stats.pass > 0) {
          descs += ('failures and passes');
        } else {
          descs += ('only failures');
        }
      } else if (stats.warning > 0) {
        if (stats.pass > 0) {
          descs += ('warnings and passes');
        } else {
          descs += ('only warnings');
        }
      } else if (stats.pass > 0) {
        descs += ('only passes');
      } else {
        return 'We have no data for this hour';
      }
      descs += (' during this hour.');
      return descs;
    },
    resultsPath(stats) {
      const time = this.$moment.utc(stats.date).add(1, 'hours').format('YYYY-MM-DD+HH[%3A]mm[%3A]00');
      if (stats.failure || stats.warning) {
        return `/tests/results/?time=${time}&result_filter=10&result_filter=30&result_filter=40&result_filter=50&result_filter=60&result_filter=100&result_filter=110&result_filter=120&result_filter=140&result_filter=150&http_filter=&remote_location_filter=__ALL__`;
      }
      return `/tests/results/?time=${time}&remote_location_filter=__ALL__`;
    },
  },
};
</script>
